// cesium.ts - Main Cesium implementation file

import { 
  Ion, 
  Viewer, 
  Terrain, 
  Color, 
  CameraEventType, 
  ScreenSpaceEventType,
  createOsmBuildingsAsync 
} from 'cesium';
import "cesium/Build/Cesium/Widgets/widgets.css";

import type { InitializeCesiumOptions, CesiumViewSettings } from './cesium-types';
import { 
  setupCameraEvents, 
  setCameraReady, 
  resetCameraState,
  createCameraOptions,
  flyToPosition as _flyToPosition,
  flyToView as _flyToView,
  updateCameraPosition as _updateCameraPosition,
  setCameraPosition as _setCameraPosition,
  getCurrentCameraPosition as _getCurrentCameraPosition
} from './cesium-camera';
import { 
  loadMultipleAssets, 
  setupZoomToTileset,
  findAssetById as _findAssetById,
  loadAndZoomToAsset as _loadAndZoomToAsset,
  zoomToAssetById as _zoomToAssetById,
  removeAssetById as _removeAssetById
} from './cesium-assets';
import { performanceMonitor } from './cesium-performance';

// Set Cesium Ion access token
Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI1MjI3NTNhZC0zNTljLTRlNWItOTc0ZS0yYjNjODhlZWU1OGYiLCJpZCI6MTk0OTI5LCJpYXQiOjE3MDc1MTE5NjR9.bW1to-Dax47Ra0BiGefax1eFdsOkPvWSaXSjCodQHgE';

// Global viewer instance
let viewer: any = null;
let isMobileDevice = false;

// Utility functions
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Configure viewer options based on device type
function createViewerOptions(isMobile: boolean, enablePerformanceMode: boolean = false): any {
  console.log('🔧 Creating Cesium viewer with options:', { isMobile, enablePerformanceMode });
  
  const baseOptions = {
    // Use less intensive terrain for better performance
    terrain: enablePerformanceMode ? undefined : Terrain.fromWorldTerrain(),
    timeline: false,
    animation: false,
    sceneModePicker: false,
    baseLayerPicker: false,
    infoBox: false,
  };

  if (isMobile || enablePerformanceMode) {
    console.log('📱 Mobile/Performance mode detected. Applying aggressive optimizations.');
    return {
      ...baseOptions,
      requestRenderMode: true,
      maximumRenderTimeChange: Infinity,
      scene3DOnly: true,
      shadows: false,
      terrainShadows: false,
      // Additional performance options
      contextOptions: {
        webgl: {
          alpha: false,
          depth: true,
          stencil: false,
          antialias: false,
          premultipliedAlpha: true,
          preserveDrawingBuffer: false,
          failIfMajorPerformanceCaveat: false
        }
      }
    };
  }

  // Desktop with balanced performance
  return {
    ...baseOptions,
    requestRenderMode: false, // Allow continuous rendering on desktop
    contextOptions: {
      webgl: {
        alpha: false,
        depth: true,
        stencil: false,
        antialias: true,
        premultipliedAlpha: true,
        preserveDrawingBuffer: false,
        failIfMajorPerformanceCaveat: false
      }
    }
  };
}

// Apply mobile optimizations to viewer
function applyMobileOptimizations(viewer: any) {
  if (!isMobileDevice || !viewer) return;

  viewer.scene.globe.maximumScreenSpaceError = 8.0;
  const pixelRatio = window.devicePixelRatio || 1;
  
  if (pixelRatio >= 3) viewer.resolutionScale = 0.5;
  else if (pixelRatio >= 2) viewer.resolutionScale = 0.6;
  else viewer.resolutionScale = 0.7;
  
  viewer.scene.postProcessStages.fxaa.enabled = false;
  viewer.scene.fog.enabled = false;
  viewer.scene.skyAtmosphere.show = false;
  viewer.scene.globe.enableLighting = false;
  viewer.scene.globe.depthTestAgainstTerrain = false;
}

// Setup scene configuration
function configureScene(viewer: any) {
  viewer.scene.globe.depthTestAgainstTerrain = false;
  viewer.scene.backgroundColor = Color.BLACK;
  viewer.scene.globe.baseColor = new Color(0, 0, 0, 0);
  viewer.scene.skyAtmosphere.show = !isMobileDevice;
}

// Setup camera controls
function setupCameraControls(viewer: any) {
  viewer.scene.screenSpaceCameraController.minimumZoomDistance = 1.0;
  const controller = viewer.scene.screenSpaceCameraController;
  
  if (isMobileDevice) {
    controller.enableRotate = true;
    controller.enableTranslate = true;
    controller.enableZoom = true;
    controller.enableTilt = true;
  } else {
    controller.tiltEventTypes = [CameraEventType.RIGHT_DRAG];
    controller.rotateEventTypes = [CameraEventType.LEFT_DRAG];
    controller.zoomEventTypes = [CameraEventType.WHEEL, CameraEventType.PINCH];
    controller.translateEventTypes = [CameraEventType.MIDDLE_DRAG];
    controller.enableRotate = true;
    controller.enableTranslate = true;
    controller.enableZoom = true;
    controller.enableTilt = true;
  }
  
  controller.inertiaSpin = 0.0;
  controller.inertiaTranslate = 0.0;
  controller.inertiaZoom = 0.0;
}

// Setup click event handlers
function setupClickHandlers(viewer: any, onObjectClick?: (attributes: { [key: string]: any }) => void) {
  if (!onObjectClick) return;

  viewer.screenSpaceEventHandler.setInputAction((click: any) => {
    try {
      if (viewer) {
        const pickedObject = viewer.scene.pick(click.position);
        if (pickedObject) {
          const feature = pickedObject as any;
          const content = feature._content;
          if (content && content.batchTable && content.batchTable._propertyTable) {
            const propertyTable = content.batchTable._propertyTable;
            const metadataTable = propertyTable._metadataTable;
            if (metadataTable && typeof metadataTable.getProperty === 'function') {
              try {
                const propertyIds = metadataTable.getPropertyIds();
                const attributes: { [key: string]: any } = {};
                const batchId = feature._batchId;
                if (propertyIds && Array.isArray(propertyIds)) {
                  propertyIds.forEach((id: string) => {
                    try {
                      attributes[id] = metadataTable.getProperty(batchId, id);
                    } catch (propError) {
                      console.warn(`Failed to get property ${id}:`, propError);
                    }
                  });
                  onObjectClick(attributes);
                }
              } catch (metadataError) {
                console.warn('Error accessing metadata properties:', metadataError);
              }
            }
          }
        }
      }
    } catch (clickError) {
      console.warn('Error in click handler:', clickError);
    }
  }, ScreenSpaceEventType.LEFT_CLICK);
}

// Setup global event listeners
function setupGlobalEvents(viewer: any) {
  window.addEventListener('depthTestChange', ((event: CustomEvent) => {
    if (viewer) viewer.scene.globe.depthTestAgainstTerrain = event.detail.enabled;
  }) as EventListener);

  window.addEventListener('shadowsChange', ((event: CustomEvent) => {
    if (viewer) viewer.scene.globe.enableLighting = event.detail.enabled;
  }) as EventListener);
}

// Load OSM buildings for non-mobile devices
async function loadOsmBuildings(viewer: any) {
  if (isMobileDevice) return;
  
  try {
    const buildingTileset = await createOsmBuildingsAsync();
    if (viewer) viewer.scene.primitives.add(buildingTileset);
  } catch (error) {
    console.warn('Failed to load OSM buildings:', error);
  }
}

// Main initialization function
export async function initializeCesium(options: InitializeCesiumOptions = {}): Promise<void> {
  const startTime = performance.now();
  console.log('🚀 Starting Cesium initialization...');

  try {
    isMobileDevice = isMobile();
    const hasInitialCameraPosition = !!options.initialCameraPosition;
    
    // Detect if we should enable performance mode based on device capabilities
    const enablePerformanceMode = shouldEnablePerformanceMode();
    
    console.log('📊 Device analysis:', {
      isMobile: isMobileDevice,
      enablePerformanceMode,
      devicePixelRatio: window.devicePixelRatio,
      hardwareConcurrency: navigator.hardwareConcurrency || 'unknown'
    });

    // Create and configure viewer
    const viewerOptions = createViewerOptions(isMobileDevice, enablePerformanceMode);
    viewer = new Viewer('cesiumContainer', viewerOptions);

    if (!viewer) {
      console.error("❌ Failed to initialize Cesium viewer.");
      return;
    }

    console.log('✅ Cesium viewer created successfully');

    // Initialize performance monitoring
    performanceMonitor.initialize(viewer);

    // Configure scene and optimizations
    configureScene(viewer);
    applyMobileOptimizations(viewer);
    setupCameraControls(viewer);

    // Setup zoom to tileset functionality
    setupZoomToTileset(viewer, hasInitialCameraPosition, options.autoZoomToAssets);

    // Setup event handlers
    setupCameraEvents(viewer, options.onCameraMove);
    setupClickHandlers(viewer, options.onObjectClick);
    setupGlobalEvents(viewer);

    // Set initial camera position if provided
    if (options.initialCameraPosition) {
      const cameraOptions = createCameraOptions(options.initialCameraPosition, 2.0);
      viewer.camera.flyTo(cameraOptions);
      
      // Show the globe after camera is positioned
      viewer._initialFlightStarted = true;
      viewer.scene.backgroundColor = Color.TRANSPARENT;
      viewer.scene.globe.baseColor = Color.TRANSPARENT;
    }

    // Load OSM buildings (only if not in performance mode)
    if (!enablePerformanceMode) {
      console.log('🏢 Loading OSM buildings...');
      await loadOsmBuildings(viewer);
    } else {
      console.log('⚡ Skipping OSM buildings in performance mode');
    }

    // Load assets
    const assetIds = options.assetIds || [];
    if (assetIds.length > 0) {
      console.log(`📦 Loading ${assetIds.length} assets...`);
      try {
        await loadMultipleAssets(viewer, assetIds, isMobileDevice);
        
        // Only auto-zoom to first asset if no initial camera position or autoZoomToAssets is not false
        if ((!hasInitialCameraPosition || options.autoZoomToAssets !== false) && assetIds.length > 0) {
          const firstTileset = _findAssetById(viewer, assetIds[0]);
          if (firstTileset) {
            viewer.zoomToTileset(firstTileset);
          }
        }
      } catch (error) {
        console.error('❌ Error loading assets:', error);
      }
    }

    // Mark as ready
    setCameraReady(true);
    
    const initTime = performance.now() - startTime;
    console.log(`🎉 Cesium initialization completed in ${initTime.toFixed(2)}ms`);
    
    // Show initial performance report
    setTimeout(() => {
      performanceMonitor.showPerformanceReport();
    }, 3000);

    options.onReady?.();

  } catch (error: any) {
    console.error('❌ Error initializing Cesium:', error);
    console.error('Stack trace:', error?.stack);
  }
}

// Helper function to determine if performance mode should be enabled
function shouldEnablePerformanceMode(): boolean {
  // Check device capabilities
  const devicePixelRatio = window.devicePixelRatio || 1;
  const hardwareConcurrency = navigator.hardwareConcurrency || 4;
  
  // Enable performance mode if:
  // - High pixel ratio (retina displays consume more GPU)
  // - Low hardware concurrency (fewer CPU cores)
  // - Mobile device
  // - User agent suggests low-end device
  
  const hasHighPixelRatio = devicePixelRatio > 2;
  const hasLowConcurrency = hardwareConcurrency < 4;
  const isLowEndDevice = /Android.*Chrome\/[0-9]{2}/.test(navigator.userAgent);
  
  return hasHighPixelRatio || hasLowConcurrency || isLowEndDevice || isMobileDevice;
}

// Wrapper for flyToPosition to maintain original API
export function flyToPosition(flyToSettings: CesiumViewSettings, cesiumCameraPosition?: CesiumViewSettings) {
  return _flyToPosition(flyToSettings, cesiumCameraPosition, viewer);
}

// Public API functions
export function getViewer() {
  return viewer;
}

export function resetViewerState() {
  resetCameraState();
}

// Public API camera functions that use the internal viewer
export function flyToView(settings: CesiumViewSettings, duration: number = 2.0, onComplete?: () => void) {
  return _flyToView(viewer, settings, duration, onComplete);
}

export function updateCameraPosition(settings: CesiumViewSettings, duration: number = 2.0, onComplete?: () => void) {
  return _updateCameraPosition(viewer, settings, duration, onComplete);
}

export function setCameraPosition(settings: CesiumViewSettings) {
  return _setCameraPosition(viewer, settings);
}

export function getCurrentCameraPosition(): CesiumViewSettings | null {
  return _getCurrentCameraPosition(viewer);
}

// Public API asset functions that use the internal viewer
export async function loadAndZoomToAsset(assetId: number, duration?: number) {
  return _loadAndZoomToAsset(viewer, assetId, duration);
}

export function zoomToAssetById(assetId: number, duration?: number) {
  return _zoomToAssetById(viewer, assetId, duration);
}

export function removeAssetById(assetId: number) {
  return _removeAssetById(viewer, assetId);
}

export function findAssetById(assetId: number) {
  return _findAssetById(viewer, assetId);
}

// Export performance monitoring functions for developer console access
export function showPerformanceReport() {
  return performanceMonitor.showPerformanceReport();
}

export function enablePerformanceMode() {
  performanceMonitor.enablePerformanceMode();
  console.log('🔧 Performance mode enabled from external call');
}

export function enableBalancedMode() {
  performanceMonitor.enableBalancedMode();
  console.log('⚖️ Balanced mode enabled from external call');
}

export function enableQualityMode() {
  performanceMonitor.enableQualityMode();
  console.log('✨ Quality mode enabled from external call');
}

export function enableEmergencyMode() {
  performanceMonitor.enableEmergencyMode();
  console.log('🚨 Emergency mode enabled from external call');
}

export function unloadExcessTilesets(maxTilesets: number = 3) {
  performanceMonitor.unloadExcessTilesets(maxTilesets);
  console.log(`🗑️ Tileset cleanup requested, keeping max ${maxTilesets} tilesets`);
}

export function getPerformanceMetrics() {
  return performanceMonitor.getMetrics();
}

// Global window exposure for easy console access
if (typeof window !== 'undefined') {
  (window as any).cesiumPerformance = {
    showReport: showPerformanceReport,
    enablePerformanceMode,
    enableBalancedMode,
    enableQualityMode,
    enableEmergencyMode,
    unloadExcessTilesets,
    getMetrics: getPerformanceMetrics,
    monitor: performanceMonitor
  };
  
  console.log('🔧 Cesium performance tools exposed to window.cesiumPerformance');
  console.log('🆘 For extreme performance issues, try: window.cesiumPerformance.enableEmergencyMode()');
  console.log('📦 To reduce tilesets, try: window.cesiumPerformance.unloadExcessTilesets(2)');
}

// Re-export types
export type {
  CesiumViewSettings,
  InitializeCesiumOptions,
  StyleCondition,
  CameraOptions
} from './cesium-types';

// DOM event handlers for settings
document.addEventListener("DOMContentLoaded", function () {
  const depthTestCheckbox = document.getElementById("toggleDepthTest") as HTMLInputElement;
  const shadowsCheckbox = document.getElementById("toggleShadows") as HTMLInputElement;

  function updateSettings() {
    if (viewer) {
      viewer.scene.globe.depthTestAgainstTerrain = depthTestCheckbox.checked;
      viewer.shadows = shadowsCheckbox.checked;
    }
  }

  if (depthTestCheckbox && shadowsCheckbox) {
    depthTestCheckbox.addEventListener("change", updateSettings);
    shadowsCheckbox.addEventListener("change", updateSettings);
  }
}); 